import { useCallback, useEffect, useRef, useState } from 'react';

import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import AcademiaSelect from '../common/AcademiaSelect';
import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import AdminToast from '../admin/AdminToast';
import { apiRequest } from '../../services/api';
import DocumentPreviewModal from '../common/DocumentPreviewModal';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import {
  deleteProfileDocument,
  fetchDocumentTypes,
  fetchDossierStatut,
  fetchMyDocuments,
  registerProfileDocument,
} from '../../services/academiaApprenantService';
import {
  APPRENANT_DOCUMENT_LABELS,
  IDENTITY_DOCUMENT_LABELS,
  IDENTITY_DOCUMENT_TYPES,
  isIdentityDocumentType,
} from '../../constants/academiaDocumentTypes';

type DocRow = {
  id: string;
  typeDocument: string;
  urlFichier: string;
  statutValidation?: string;
  commentaireAdmin?: string | null;
  dateCreation?: string;
};

const STATUT_LABELS: Record<string, string> = {
  EN_ATTENTE: 'En attente',
  VALIDE: 'Validé',
  REJETE: 'Rejeté',
};

const TYPE_LABELS: Record<string, string> = APPRENANT_DOCUMENT_LABELS;

function formatShortDate(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function statutBadgeClass(statut?: string) {
  if (statut === 'VALIDE') return 'apprenant-dossier-doc__badge--valid';
  if (statut === 'REJETE') return 'apprenant-dossier-doc__badge--rejected';
  return 'apprenant-dossier-doc__badge--pending';
}

function documentTypeIcon(type: string) {
  if (isIdentityDocumentType(type)) return 'badge';
  if (type === 'PHOTO') return 'image';
  return 'description';
}

type DossierState = {
  complet: boolean;
  message?: string;
  messageInscription?: string;
  identityDepotAutorise?: boolean;
  identityStatut?: string;
};

type ToastState = { message: string; variant: 'success' | 'error' } | null;

export default function ApprenantDocumentsPanel() {
  const [documents, setDocuments] = useState<DocRow[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [identityTypes, setIdentityTypes] = useState<string[]>([...IDENTITY_DOCUMENT_TYPES]);
  const [typeDocument, setTypeDocument] = useState('PIECE_IDENTITE');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [dossierStatut, setDossierStatut] = useState<DossierState | null>(null);
  const [preview, setPreview] = useState<{
    title: string;
    apiPath: string;
  } | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [docs, typesPayload, dossier] = await Promise.all([
        fetchMyDocuments(),
        fetchDocumentTypes(),
        fetchDossierStatut().catch(() => null),
      ]);
      setDocuments(docs);
      setDossierStatut(dossier);
      const t = typesPayload?.types ?? typesPayload;
      setTypes(Array.isArray(t) && t.length ? t : Object.keys(TYPE_LABELS));
      if (Array.isArray(typesPayload?.identityTypes) && typesPayload.identityTypes.length) {
        setIdentityTypes(typesPayload.identityTypes);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useRegisterPortalRefresh(() => void reload());

  const pendingCount = documents.filter((d) => d.statutValidation === 'EN_ATTENTE').length;
  const identityUploadBlocked =
    isIdentityDocumentType(typeDocument) && dossierStatut?.identityDepotAutorise === false;
  const otherTypes = types.filter((t) => !identityTypes.includes(t));

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const handleFile = async (file: File) => {
    if (identityUploadBlocked) {
      showToast(
        dossierStatut?.identityStatut === 'VALIDE'
          ? 'Votre justificatif d\'identité est déjà validé. Vous ne pouvez pas en déposer un autre.'
          : 'Vous avez déjà déposé un justificatif d\'identité en cours de validation.',
        'error',
      );
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await apiRequest('/auth/upload-registration-document', {
        method: 'POST',
        withAuth: true,
        body: form,
      });
      const path = uploadRes?.data?.path || uploadRes?.data?.url;
      const urlFichier =
        typeof path === 'string' && path.startsWith('/uploads/')
          ? path
          : typeof uploadRes?.data?.url === 'string'
            ? new URL(uploadRes.data.url).pathname
            : null;
      const uploadToken = uploadRes?.data?.uploadToken;
      if (!urlFichier) throw new Error('Réponse upload invalide');

      await registerProfileDocument({ typeDocument, urlFichier, uploadToken });
      await reload();
      const label = TYPE_LABELS[typeDocument] || typeDocument;
      showToast(
        `« ${label} » a été déposé avec succès. Il sera examiné par l'administration.`,
        'success',
      );
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Téléversement impossible', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProfileDocument(id);
      await reload();
      showToast('Document supprimé avec succès.', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Suppression impossible', 'error');
    }
  };

  return (
    <ApprenantPageShell
      title="Mon dossier"
      description="Identité et photo — validation admin requise avant inscription."
      sideCard={{
        label: 'Documents déposés',
        value: loading ? '…' : String(documents.length),
        helper: dossierStatut?.complet
          ? 'Dossier validé — inscriptions ouvertes'
          : pendingCount > 0
            ? `${pendingCount} en attente de validation`
            : 'Complétez les pièces obligatoires',
        sideLinks: [
          { label: 'Formations', to: '/app/formations', icon: 'school' },
          { label: 'Accueil', to: '/app', icon: 'dashboard' },
        ],
      }}
      onRetry={() => void reload()}
      isLoading={loading}
      isError={loadError}
      loadingMessage="Chargement de votre dossier…"
      errorMessage="Impossible de charger votre dossier."
    >
      {dossierStatut && !dossierStatut.complet ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-bold">Dossier incomplet</p>
          <p className="mt-1">{dossierStatut.message}</p>
        </div>
      ) : dossierStatut?.complet ? (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
          Dossier validé — vous pouvez vous inscrire à une cohorte.
        </p>
      ) : null}

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="apprenant-dossier-panel" aria-labelledby="apprenant-dossier-upload-heading">
          <header className="apprenant-dossier-panel__header">
            <span className="apprenant-dossier-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="upload" tone="inherit" size={22} />
            </span>
            <div className="min-w-0">
              <h2 id="apprenant-dossier-upload-heading" className="apprenant-dossier-panel__title">
                Ajouter un document
              </h2>
              <p className="apprenant-dossier-panel__subtitle">
                CNI, passeport, permis ou carte d&apos;électeur + photo · un dépôt à la fois, validation par
                l&apos;administration
              </p>
            </div>
          </header>
          <div className="apprenant-dossier-panel__body">
            {dossierStatut?.identityStatut === 'REJETE' ? (
              <div className="apprenant-dossier-inline-alert apprenant-dossier-inline-alert--error">
                <LucideSymbolIcon name="info" tone="inherit" size={18} />
                <p>
                  Votre justificatif d&apos;identité a été rejeté. Vous pouvez en déposer un nouveau (une seule
                  soumission).
                </p>
              </div>
            ) : dossierStatut?.identityDepotAutorise === false &&
              dossierStatut.identityStatut !== 'VALIDE' ? (
              <div className="apprenant-dossier-inline-alert apprenant-dossier-inline-alert--warn">
                <LucideSymbolIcon name="schedule" tone="inherit" size={18} />
                <p>
                  Justificatif d&apos;identité déjà déposé — en attente de validation par l&apos;administration.
                </p>
              </div>
            ) : null}

            <label className="apprenant-dossier-upload__field" htmlFor="apprenant-dossier-type">
              <span className="apprenant-dossier-upload__label">Type de document</span>
              <AcademiaSelect
                id="apprenant-dossier-type"
                menuPlacement="top"
                value={typeDocument}
                onChange={(e) => setTypeDocument(e.target.value)}
              >
                <option disabled value="__identity_group">
                  Justificatif d&apos;identité (catégorie d&apos;âge)
                </option>
                {identityTypes.map((t) => (
                  <option key={t} value={t} disabled={dossierStatut?.identityDepotAutorise === false}>
                    {IDENTITY_DOCUMENT_LABELS[t as keyof typeof IDENTITY_DOCUMENT_LABELS] ||
                      TYPE_LABELS[t] ||
                      t}
                  </option>
                ))}
                {otherTypes.length ? (
                  <option disabled value="__other_group">
                    Autres pièces
                  </option>
                ) : null}
                {otherTypes.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t] || t}
                  </option>
                ))}
              </AcademiaSelect>
            </label>

            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              disabled={uploading || identityUploadBlocked}
              onClick={() => fileRef.current?.click()}
              className="apprenant-dossier-upload__zone"
            >
              <span className="apprenant-dossier-upload__zone-icon" aria-hidden>
                <LucideSymbolIcon name="upload" tone="primary" size={28} />
              </span>
              <span className="apprenant-dossier-upload__zone-title">
                {uploading
                  ? 'Téléversement en cours…'
                  : identityUploadBlocked
                    ? 'Dépôt identité indisponible'
                    : 'Cliquez pour choisir un fichier'}
              </span>
              <span className="apprenant-dossier-upload__zone-hint">
                {identityUploadBlocked
                  ? 'Un justificatif est déjà en cours de traitement ou validé.'
                  : 'Formats acceptés : JPG, PNG, PDF, TXT'}
              </span>
            </button>
          </div>
        </section>

        <section className="apprenant-dossier-panel" aria-labelledby="apprenant-dossier-list-heading">
          <header className="apprenant-dossier-panel__header">
            <span className="apprenant-dossier-panel__header-icon" aria-hidden>
              <LucideSymbolIcon name="folder" tone="inherit" size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="apprenant-dossier-list-heading" className="apprenant-dossier-panel__title">
                Documents enregistrés
              </h2>
              <p className="apprenant-dossier-panel__subtitle">
                {documents.length} document{documents.length !== 1 ? 's' : ''}
                {pendingCount > 0 ? ` · ${pendingCount} en attente` : ''}
              </p>
            </div>
          </header>
          <div className="apprenant-dossier-panel__body">
            {documents.length === 0 ? (
              <div className="apprenant-dossier-empty">
                <LucideSymbolIcon name="folder_shared" tone="primary" size={40} className="opacity-80" />
                <p>Aucun document pour le moment.</p>
                <p className="text-xs">Déposez votre pièce d&apos;identité pour activer les inscriptions.</p>
              </div>
            ) : (
              <ul className="apprenant-dossier-docs">
                {documents.map((d) => {
                  const label = TYPE_LABELS[d.typeDocument] || d.typeDocument;
                  const deleteDisabled =
                    isIdentityDocumentType(d.typeDocument) && d.statutValidation === 'EN_ATTENTE';
                  return (
                    <li key={d.id} className="apprenant-dossier-doc">
                      <div className="apprenant-dossier-doc__accent" aria-hidden />
                      <div className="apprenant-dossier-doc__inner">
                        <header className="apprenant-dossier-doc__head">
                          <span className="apprenant-dossier-doc__icon">
                            <LucideSymbolIcon
                              name={documentTypeIcon(d.typeDocument)}
                              tone="inherit"
                              size={20}
                            />
                          </span>
                          <div className="min-w-0 flex-1">
                            <h3 className="apprenant-dossier-doc__title">{label}</h3>
                            {formatShortDate(d.dateCreation) ? (
                              <p className="apprenant-dossier-doc__date">
                                Déposé le {formatShortDate(d.dateCreation)}
                              </p>
                            ) : null}
                          </div>
                          {d.statutValidation ? (
                            <span
                              className={`apprenant-dossier-doc__badge ${statutBadgeClass(d.statutValidation)}`}
                            >
                              {STATUT_LABELS[d.statutValidation] ?? d.statutValidation}
                            </span>
                          ) : null}
                        </header>

                        {d.commentaireAdmin ? (
                          <p className="apprenant-dossier-doc__comment">{d.commentaireAdmin}</p>
                        ) : null}

                        <footer className="apprenant-dossier-doc__actions">
                          <button
                            type="button"
                            onClick={() =>
                              setPreview({
                                title: label,
                                apiPath: `/profil/documents/${encodeURIComponent(d.id)}/fichier`,
                              })
                            }
                            className="apprenant-dossier-doc__btn apprenant-dossier-doc__btn--outline"
                          >
                            <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
                            Voir
                          </button>
                          <button
                            type="button"
                            onClick={() => setDocumentToDelete(d.id)}
                            disabled={deleteDisabled}
                            title={
                              deleteDisabled
                                ? 'Impossible de supprimer un justificatif en cours de validation'
                                : undefined
                            }
                            className="apprenant-dossier-doc__btn apprenant-dossier-doc__btn--danger"
                          >
                            <LucideSymbolIcon name="delete_forever" tone="inherit" size={16} />
                            Supprimer
                          </button>
                        </footer>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      <DocumentPreviewModal
        open={preview !== null}
        apiPath={preview?.apiPath ?? ''}
        title={preview?.title ?? 'Document'}
        onClose={() => setPreview(null)}
      />
      <DeleteConfirmModal
        open={documentToDelete !== null}
        title="Supprimer ce document ?"
        description="Cette action est irréversible. Le document sera retiré de votre dossier."
        onCancel={() => setDocumentToDelete(null)}
        onConfirm={() => {
          if (!documentToDelete) return;
          void handleDelete(documentToDelete).then(() => setDocumentToDelete(null));
        }}
      />
    </ApprenantPageShell>
  );
}
